import { FilterOutlined } from "@ant-design/icons";
import { Checkbox, Flex } from "antd";
import { FC, useCallback, useMemo } from "react";
import {
    Point,
    SubtaskProductsMatrix,
    TaskAssortmentMatrix,
} from "tm-shared/types";

import { useCatalog } from "@/domains/product";
import { ForceStepComplete } from "@/features/ForceStepComplete";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { AppSelect } from "@/shared/forms/AppSelect";
import { PrimaryText } from "@/shared/ui/Typography";
import { setAssortmentMatrixSubtaskPayload } from "@/store/slices/progress";
import { setMdTasksConfig } from "@/store/slices/ui";

import {
    getNextSubtaskId,
    makeForceStepCompletePayload,
} from "../../../AssortmentMatrix.utils";

interface ProductsMatrixControllerProps {
    subtask: SubtaskProductsMatrix;
    point: Point;
    task: TaskAssortmentMatrix;
}

const PLACEHOLDER_TEXT = "Детальна група";
const LABEL_TEXT = "Показати невиконані";

export const ProductsMatrixController: FC<ProductsMatrixControllerProps> = ({
    subtask,
    point,
    task,
}) => {
    const dispatch = useAppDispatch();

    const pointId = point.id;
    const taskId = task.id;
    const subtaskId = subtask.subtaskId;

    // Селектори
    const tasksConfig = useAppSelector((state) => state.ui.mdTasksConfig);
    const { products } = useCatalog();

    // Конфігурація поточної підзадачі
    const currentSubtaskConfig = useMemo(
        () => tasksConfig?.[pointId]?.[taskId]?.subtasks?.[subtaskId],
        [tasksConfig, pointId, taskId, subtaskId],
    );

    // Фінансові групи товарів
    const finGroupsFromProducts = useMemo(
        () =>
            subtask.context.products
                .map(
                    (barcode) =>
                        products[barcode]?.params?.fin_group_detail?.value,
                )
                .filter(Boolean), // Видаляємо undefined/null значення
        [products, subtask.context.products],
    );

    const uniqueFinGroups = useMemo(
        () => Array.from(new Set(finGroupsFromProducts)),
        [finGroupsFromProducts],
    );

    // Поточні значення фільтрів
    const selectedFinGroup = useMemo(
        () => currentSubtaskConfig?.chosenFingroup ?? null,
        [currentSubtaskConfig?.chosenFingroup],
    );

    const shouldShowUncompleted = useMemo(
        () => currentSubtaskConfig?.showUncompleted ?? false,
        [currentSubtaskConfig?.showUncompleted],
    );

    // Базові параметри для dispatch
    const baseTaskConfigParams = useMemo(
        () => ({
            pointId,
            taskId,
            subtaskId,
        }),
        [pointId, taskId, subtaskId],
    );

    // Функція для скидання активного товару
    const resetActiveProduct = useCallback(() => {
        dispatch(
            setMdTasksConfig({
                ...baseTaskConfigParams,
                value: null,
            }),
        );
    }, [dispatch, baseTaskConfigParams]);

    // Обробники подій
    const handleFinGroupChange = useCallback(
        (selectedValue: string) => {
            const finGroupValue = selectedValue || null;

            // Встановлюємо нову фінансову групу
            dispatch(
                setMdTasksConfig({
                    ...baseTaskConfigParams,
                    field: "chosenFingroup",
                    value: finGroupValue,
                }),
            );

            // Скидаємо активний товар при зміні групи
            resetActiveProduct();
        },
        [dispatch, baseTaskConfigParams, resetActiveProduct],
    );

    const handleShowUncompletedChange = useCallback(
        (isChecked: boolean) => {
            // Встановлюємо прапорець показу невиконаних
            dispatch(
                setMdTasksConfig({
                    ...baseTaskConfigParams,
                    field: "showUncompleted",
                    value: isChecked,
                }),
            );

            // Скидаємо активний товар при зміні фільтра
            resetActiveProduct();
        },
        [dispatch, baseTaskConfigParams, resetActiveProduct],
    );

    // Placeholder для Select
    const selectPlaceholder = useMemo(
        () => (
            <>
                <FilterOutlined />
                &nbsp; {PLACEHOLDER_TEXT}
            </>
        ),
        [],
    );

    const handleForceStepComplete = useCallback(
        async (comment: string) => {
            const payload = makeForceStepCompletePayload({
                subtask,
                comment,
            });

            if (!payload) return;

            dispatch(
                setAssortmentMatrixSubtaskPayload({
                    taskId,
                    subtaskId,
                    payload,
                }),
            );

            const { nextSubtaskId } = getNextSubtaskId({
                subtasks: task.context.subtasks,
                currentSubtaskId: subtaskId,
            });

            if (nextSubtaskId) {
                dispatch(
                    setMdTasksConfig({
                        pointId,
                        taskId,
                        field: "activeItem",
                        value: nextSubtaskId,
                    }),
                );
            }
        },
        [dispatch, pointId, subtask, subtaskId, task.context.subtasks, taskId],
    );

    const finGroupOptions = uniqueFinGroups.map((finGroup) => ({
        label: finGroup,
        value: finGroup,
    }));

    return (
        <Flex vertical gap={16}>
            <AppSelect
                value={selectedFinGroup}
                onChange={handleFinGroupChange}
                listHeight={400}
                allowClear
                size="large"
                style={{ width: "100%" }}
                popupMatchSelectWidth={false}
                placeholder={selectPlaceholder}
                options={finGroupOptions}
            />

            <Flex align="center" justify="space-between" gap={8}>
                <Checkbox
                    checked={shouldShowUncompleted}
                    onChange={(event) =>
                        handleShowUncompletedChange(event.target.checked)
                    }
                >
                    <PrimaryText>{LABEL_TEXT}</PrimaryText>
                </Checkbox>

                <ForceStepComplete
                    onComplete={handleForceStepComplete}
                    taskName={task.name}
                    pointName={point.name}
                    entity="step"
                />
            </Flex>
        </Flex>
    );
};
